import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { CSSProperties } from 'react';
import { radiusHalf, radiusHalfPlus } from 'utility/borderRadius';

export const audioDropzoneHeight = 200;
export const regularDropzoneHeight = 450;

export const assetActionButtonSx = (theme: Theme): SystemStyleObject => ({
  whiteSpace: 'nowrap',
  '& .MuiButton-endIcon': {
    p: 0,
    m: 0,
    ml: 0.5,
    color: theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    '& .MuiButton-endIcon': {
      color: theme.palette.text.disabled,
    },
  },
});

export const urlDialogSx = (theme: Theme): SystemStyleObject => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
  },
  '& .MuiDialogTitle-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    p: 2,
  },
  '& .MuiDialogContent-root': {
    pt: 2,
    pb: 0,
  },
  '& .MuiDialogActions-root': {
    p: 2,
  },
});

export const urlDialogTextFieldSx = (theme: Theme): SystemStyleObject => ({
  '& .MuiFormHelperText-root': {
    mx: 0,
  },
});

export const urlDialogActionsSx = (theme: Theme): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
  gap: theme.spacing(1),
});

export const placeholderWithVideoBoxSx = (
  theme: Theme,
  isDragActive: boolean,
): CSSProperties => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: radiusHalfPlus(theme, 2),
  position: 'relative',
  filter: isDragActive ? 'brightness(50%)' : 'none',
});

export const helperTextSx = (theme: Theme): SystemStyleObject => ({
  display: 'inline-flex',
  py: 0.25,
  minHeight: 12,
  fontSize: 12,
  color: theme.palette.text.secondary,
});

export const richTextInputSx = (theme: Theme): SystemStyleObject => ({
  mt: 1,
  fontSize: '16px',
  '& .input-field .tiptap.ProseMirror': {
    px: 1,
    borderRadius: radiusHalf(theme),
    backgroundColor: theme.palette.action.hover,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: theme.palette.action.hover,
    '&:focus': {
      borderColor: `${theme.palette.primary.main} !important`,
    },
  },
});

export const placeholderEmptyTypographySx = (theme: Theme): SystemStyleObject => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  ml: 0.25,
  mt: 1.2,
});

export const emptyUploadIconSx = (theme: Theme): SystemStyleObject => ({
  fontSize: 40,
  height: 40,
});

/**
 * Note: pointerEvents: 'none' is set on these to prevent
 * flickering drag/drop message when dragging over the dropzone.
 *
 * If you need to add interactive elements on top of the dropzone
 * (eg: Audio Players, View Fullsize buttons, other controls) you
 * will need to exempt that element from pointerEvents and prop drill
 * isDragActive to conditionally apply "pointerEvents: 'none/auto'
 * based on the drag active state. Also if prop drilling, you likely
 * will need to apply the to children elem css due to event bubbling
 * to prevent any inconsistent drag-drop message flicker issues. To test
 * drag file and rapidly move in and out of the dz area checking consistency.
 *
 * Note additional isHoverActive / setIsHoverActive props passed that
 * are set onPointerEnter/Leave via the parent root dropzone wrapper.
 * It can be used for some lighter css styling but note if the
 * action opens a modal (something that blocks the leave event) then
 * you may need to manually setIsHoverActive(false) on the action handler.
 * So it should only be used for some basic css styling state
 * and not relied upon for any complex interactive controls.
 *
 */

export const getPlaceholderEmptyBoxSx = (theme: Theme): SystemStyleObject => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'row',
  fontSize: 12,
  color: theme.palette.text.secondary,
  pointerEvents: 'none',
});

export const dragAndDropMessageSx = (theme: Theme): SystemStyleObject => ({
  position: 'absolute',
  zIndex: 2,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.50)',
  backdropFilter: 'blur(3px)',
  borderRadius: radiusHalf(theme),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: 20,
  textAlign: 'center',
  pointerEvents: 'none',
});

export const placeholderPreviewWrapperSx = (theme: Theme): SystemStyleObject => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  maxHeight: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: 'none',
});

export const placeholderBlurredBgImgBoxSx = (
  theme: Theme,
  bgUrl?: string,
): SystemStyleObject => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'brightness(45%)',
  overflow: 'hidden',
  borderRadius: '4px',
  pointerEvents: 'none',
  '::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: 'blur(4px)', // Apply blur effect here
    backdropFilter: 'blur(4px)', // Apply blur effect here
  },
});

export const placeholderPreviewImgBoxSx = (
  theme: Theme,
  bgUrl?: string,
): SystemStyleObject => ({
  backdropFilter: 'blur(6px)',
  filter: 'brightness(80%)',
  position: 'absolute',
  width: '100%',
  height: '100%',
  backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  borderRadius: '4px',
  pointerEvents: 'none',
  '::before': {
    content: '""',
    position: 'absolute',
  },
});
